import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { useCallback } from "react";

import { SHIPPING_PAPERWORK_QUERY } from "./api";
import {
  APPLY_EXTRACTED_BILL_OF_LADING,
  EXTRACTED_BILL_OF_LADING,
  SHIPPING_DOCUMENT_FILE,
} from "./billOfLadingApi";
import { toApplyInput } from "./extractionReview";
import { PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY } from "./useShowFilterCounts";

/**
 * Applying a reviewed bill of lading reading.
 *
 * There is no client-side validator, so nothing runs before the mutation: the
 * apply either returns the manifest id, returns blockers (nothing was written),
 * or surfaces the server's rejection.
 *
 * Refetches the reading (its `candidateStatus` flips to APPLIED), the merged
 * shipping-paperwork list, and the counts behind the needs-review prompt.
 */
export function useApplyReviewedBillOfLading({
  documentId,
  facilityId,
  isSaved = false,
  onApplied,
}: {
  documentId: string;
  facilityId: string;
  /** The bill already has a saved manifest, so this confirm edits it. */
  isSaved?: boolean;
  onApplied: (wasteManifestId: string) => void;
}) {
  const alerts = useAlerts();
  const [applyMutation, { loading: applying }] = useMutation(APPLY_EXTRACTED_BILL_OF_LADING, {
    refetchQueries: [
      EXTRACTED_BILL_OF_LADING,
      SHIPPING_PAPERWORK_QUERY,
      PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY,
      SHIPPING_DOCUMENT_FILE,
    ],
  });

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      try {
        const { data } = await applyMutation({
          variables: { input: toApplyInput(reviewed, { documentId, facilityId }) },
        });
        const result = data?.applyExtractedBillOfLading;
        if (result?.blockers.length) {
          // The reason goes in the message itself: the alert's second argument
          // is an error object whose text sits behind the copy control.
          const reasons = result.blockers.map((blocker) => blocker.replace(/\.$/, ""));
          alerts.error(`The bill of lading was not saved. ${reasons.join(". ")}.`);
          return;
        }
        if (!result?.wasteManifestId) {
          throw new Error("The apply did not return a saved bill of lading.");
        }
        alerts.success(isSaved ? "Bill of lading saved." : "Bill of lading applied.");
        onApplied(result.wasteManifestId);
      } catch (error) {
        alerts.error(
          "Could not apply the bill of lading",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [alerts, applyMutation, documentId, facilityId, isSaved, onApplied]
  );

  return { confirm, applying };
}
